import React, { useMemo, useState } from 'react';
import { Link, Redirect, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import { LoginBackground } from '../components/auth/LoginBackground';

function readToken(search: string): string {
  const params = new URLSearchParams(search);
  return (params.get('token') || '').trim();
}

export function ResetPasswordPage() {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const token = useMemo(() => readToken(location.search), [location.search]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!loading && isAuthenticated) {
    return <Redirect to="/" />;
  }

  if (!token) {
    return (
      <div className="login-page">
        <LoginBackground />
        <div className="login-page__content flex min-h-screen items-center justify-center p-6">
          <div className="royal-card login-page__card p-6 max-w-md text-sm text-secondary-700">
          <p>This reset link is invalid or missing a token.</p>
          <Link to="/forgot-password" className="text-primary-600 font-medium mt-3 inline-block">
            Request a new link
          </Link>
        </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiService.confirmPasswordReset({
        token,
        new_password: password,
      });
      setMessage(res.data?.message || 'Password updated. You can sign in now.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <LoginBackground />
      <div className="login-page__content flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-md royal-card login-page__card p-8 shadow-royal-lg border border-primary-500/20">
          <h1 className="font-display text-xl font-bold text-secondary-900">Choose a new password</h1>
          <p className="text-sm text-secondary-600 mt-2">Use at least 8 characters.</p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-secondary-700 mb-1">
                New password
              </label>
              <input
                id="new-password"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                className="royal-input w-full px-3 py-2.5 text-sm"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-secondary-700 mb-1">
                Confirm password
              </label>
              <input
                id="confirm-password"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                className="royal-input w-full px-3 py-2.5 text-sm"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>

            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
            {message && (
              <p className="text-sm text-green-700" role="status">
                {message}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || Boolean(message)}
              className="w-full royal-btn-primary py-2.5 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {submitting ? 'Updating…' : 'Update password'}
            </button>
          </form>

          <p className="mt-6 text-sm text-secondary-600">
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
